import React, { useEffect, useState } from 'react';
import { Navbar } from '../../components/Navbar';
import { Footer } from '../../components/Footer';
import { FolderIcon, ArrowRightIcon } from 'lucide-react';
export const PreparingFilesPage = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <div className="w-full bg-gray-50 dark:bg-[#0A1C1A] min-h-screen" data-id="element-4278">
      <Navbar scrolled={scrolled} data-id="element-4279" />
      <main className="pt-24 pb-20" data-id="element-4280">
        <div className="container mx-auto px-4 md:px-6" data-id="element-4281">
          <div className="max-w-4xl mx-auto" data-id="element-4282">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] rounded-xl p-8 md:p-12 mb-12 text-white relative overflow-hidden" data-id="element-4283">
              <div className="absolute right-0 bottom-0 opacity-10" data-id="element-4284">
                <FolderIcon className="h-64 w-64" data-id="element-4285" />
              </div>
              <div className="relative z-10" data-id="element-4286">
                <h1 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-4287">
                  Preparing Your Files for Upload
                </h1>
                <p className="text-xl opacity-90 max-w-3xl" data-id="element-4288">
                  Discover how to organize and prepare your files properly so
                  buyers can easily access and use your product.
                </p>
              </div>
            </div>
            {/* Main content */}
            <div className="bg-white dark:bg-[#162825] rounded-xl p-8 md:p-10 shadow-sm mb-10" data-id="element-4289">
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6 text-lg leading-relaxed" data-id="element-4290">
                Preparing your files correctly before uploading your product can
                save you time and improve the buyer experience.
              </p>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6 text-lg leading-relaxed" data-id="element-4291">
                Start by organizing your files clearly. Group related files
                together and use simple, descriptive names so buyers can easily
                understand what each file is for.
              </p>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6 text-lg leading-relaxed" data-id="element-4292">
                Make sure your main product file includes everything needed to
                use your product. Avoid missing assets or incomplete files, as
                this can lead to confusion and negative feedback.
              </p>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6 text-lg leading-relaxed" data-id="element-4293">
                If your product includes multiple components, consider
                structuring them into folders. For example, you can separate
                source files, final versions, and documentation.
              </p>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6 text-lg leading-relaxed" data-id="element-4294">
                It's also important to include a guide or instructions if your
                product requires setup. Even a short readme file can make a big
                difference in helping buyers get started.
              </p>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6 text-lg leading-relaxed" data-id="element-4295">
                Check file sizes and formats before uploading. Make sure your
                files meet the platform requirements and are easy to download
                and open.
              </p>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6 text-lg leading-relaxed" data-id="element-4296">
                Finally, review everything from a buyer's perspective. Ask
                yourself: if someone downloads this product for the first time,
                will they understand how to use it without confusion?
              </p>
              <div className="border-l-4 border-[#2DB489] dark:border-[#4ED1A0] pl-6 my-8 py-2" data-id="element-4297">
                <p className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9] italic" data-id="element-4298">
                  Well-prepared files make your product look more professional
                  and improve the overall experience for your customers.
                </p>
              </div>
            </div>
            {/* Related resources */}
            <div className="bg-white dark:bg-[#162825] rounded-xl p-8 shadow-sm mb-10" data-id="element-4299">
              <h2 className="text-xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-4300">
                Related Resources
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4301">
                <a href="/learn/upload-first-product" className="group block bg-gray-50 dark:bg-[#1A3330] rounded-lg p-5 transition-all hover:shadow-md" data-id="element-4302">
                  <h3 className="font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2 group-hover:text-[#2DB489] dark:group-hover:text-[#4ED1A0] transition-colors" data-id="element-4303">
                    How to Upload Your First Product
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-[#B0C1BD] mb-3" data-id="element-4304">
                    Step-by-step guide to uploading and publishing your product
                    on the platform.
                  </p>
                  <div className="flex items-center text-[#2DB489] dark:text-[#4ED1A0] text-sm font-medium" data-id="element-4305">
                    <span data-id="element-4306">Read more</span>
                    <ArrowRightIcon className="ml-1 h-4 w-4 transform group-hover:translate-x-1 transition-transform" data-id="element-4307" />
                  </div>
                </a>
                <a href="/learn/professional-look" className="group block bg-gray-50 dark:bg-[#1A3330] rounded-lg p-5 transition-all hover:shadow-md" data-id="element-4308">
                  <h3 className="font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2 group-hover:text-[#2DB489] dark:group-hover:text-[#4ED1A0] transition-colors" data-id="element-4309">
                    Creating a Professional Look for Your Product
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-[#B0C1BD] mb-3" data-id="element-4310">
                    Use layout, typography, and visual consistency to build
                    trust.
                  </p>
                  <div className="flex items-center text-[#2DB489] dark:text-[#4ED1A0] text-sm font-medium" data-id="element-4311">
                    <span data-id="element-4312">Read more</span>
                    <ArrowRightIcon className="ml-1 h-4 w-4 transform group-hover:translate-x-1 transition-transform" data-id="element-4313" />
                  </div>
                </a>
              </div>
            </div>
            {/* CTA */}
            <div className="bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] rounded-xl p-8 text-white text-center" data-id="element-4314">
              <h2 className="text-2xl font-bold mb-4" data-id="element-4315">
                Files ready? Time to upload!
              </h2>
              <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto" data-id="element-4316">
                Get your product organized and published on the marketplace.
              </p>
              <a href="/sell-product" className="inline-block bg-white text-[#0D3C45] px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-all" data-id="element-4317">
                Start Selling
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-4318" />
    </div>;
};